import { useTranslation } from 'react-i18next';
import type { ReportPreviewModel } from '@/lib/patientReports/template';
import { TYPE } from '@/lib/typography';
import { cn } from '@/utils/cn';

export function ReportPreview({
  model,
  letterheadLabel,
}: {
  model: ReportPreviewModel;
  letterheadLabel: string;
}) {
  const { t } = useTranslation();
  return (
    <section aria-label={t('patientReports.preview')}>
      <h3 className={cn(TYPE.section, 'text-ds-text')}>{t('patientReports.preview')}</h3>
      <p className={cn(TYPE.small, 'mt-s1 text-ds-muted')}>{t('patientReports.preview_hint')}</p>
      <article
        data-testid="report-preview"
        className="mx-auto mt-s4 w-full max-w-[42rem] border border-ds-line bg-white px-s6 py-s6 text-[#1c1917] shadow-pop"
      >
        <p className="border-b border-neutral-200 pb-s3 text-xs uppercase tracking-wide text-neutral-500">
          {letterheadLabel}
        </p>
        {model.cover ? (
          <div className="mt-s5 border-b border-dashed border-neutral-300 pb-s6" data-preview-cover>
            <p className="text-xs uppercase tracking-wide text-neutral-500">
              {t('patientReports.cover')}
            </p>
            <h2 className="mt-s3 font-serif text-2xl">{model.cover.title || model.title}</h2>
            {model.cover.body ? (
              <p className="mt-s3 whitespace-pre-wrap text-sm leading-6">{model.cover.body}</p>
            ) : null}
          </div>
        ) : null}
        <h2 className="mt-s5 font-serif text-2xl" data-preview-title>
          {model.title || t('patientReports.untitled')}
        </h2>
        {model.blocks.length === 0 ? (
          <p className="mt-s4 text-sm text-neutral-500">{t('patientReports.preview_empty')}</p>
        ) : (
          <div className="mt-s5 flex flex-col gap-s5">
            {model.blocks.map((block) => (
              <section key={block.id}>
                {block.heading ? (
                  <h3 className="font-serif text-lg" data-preview-heading>
                    {block.heading}
                  </h3>
                ) : null}
                {block.body ? (
                  <p className="mt-s2 whitespace-pre-wrap text-sm leading-6">{block.body}</p>
                ) : null}
                {block.charts.length > 0 ? (
                  <div className="mt-s3 flex flex-col gap-s2">
                    {block.charts.map((chart) => (
                      <div key={chart.label}>
                        <div className="flex justify-between text-xs text-neutral-600">
                          <span>{chart.label}</span>
                          <span>
                            {chart.value}
                            {chart.max ? ` / ${chart.max}` : ''}
                          </span>
                        </div>
                        <div className="mt-s1 h-2 bg-neutral-200">
                          <div
                            className="h-2 bg-teal-700"
                            style={{
                              width: `${Math.max(0, Math.min(100, chart.max ? (chart.value / chart.max) * 100 : 0))}%`,
                            }}
                          />
                        </div>
                      </div>
                    ))}
                  </div>
                ) : null}
              </section>
            ))}
          </div>
        )}
      </article>
    </section>
  );
}
